import { useEffect, useMemo, useState } from 'react';
import {
  Stack, Title, Card, Group, Button, Table, Text, Checkbox, ScrollArea, Skeleton, Select,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconFileSpreadsheet, IconTrain } from '@tabler/icons-react';
import { api } from '../api/client.ts';

/**
 * Train booking report. Mirrors the legacy /train workflow:
 *   1. Date range + class + train origin (Madhuban / Delhi / Mumbai per legacy)
 *   2. Server returns the matching train travels via /api/travel-reports
 *   3. User selects rows, clicks "Download xlsx"
 *   4. Server returns a workbook from /api/train/{ids}?cls=&from=
 *
 * The class options and origin list mirror the legacy hardcoded values.
 */
interface ReportRow {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  countryName?: string | null;
  departureFromName?: string | null;
  departureDate?: string | null;
  arrivalToName?: string | null;
  arrivalDate?: string | null;
  transport?: string | null;
  transportReference?: string | null;
  [k: string]: unknown;
}

const CLASS_OPTIONS = ['AC1', 'AC2', 'AC3', 'SL'];
const TRAIN_ORIGINS = ['Madhuban', 'Delhi', 'Mumbai', 'Abu Road'];

function ymd(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function TrainBookingPage() {
  const [from, setFrom] = useState<Date | null>(new Date());
  const [to, setTo] = useState<Date | null>(addDays(new Date(), 14));
  const [cls, setCls] = useState<string | null>(CLASS_OPTIONS[1]!);
  const [origin, setOrigin] = useState<string | null>(TRAIN_ORIGINS[0]!);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);

  const queryParams = useMemo(() => {
    if (!from || !to || !origin) return null;
    return { from: ymd(from)!, to: ymd(to)!, locationFrom: origin };
  }, [from, to, origin]);

  const query = useQuery({
    queryKey: ['train-report', queryParams],
    queryFn: async (): Promise<ReportRow[]> => {
      const res = await api.get('/api/travel-reports', { params: queryParams });
      if (res.status !== 200) throw new Error(`status ${res.status}`);
      return res.data as ReportRow[];
    },
    enabled: queryParams != null,
  });
  const rows = query.data ?? [];

  // Clear selection when the filters change — otherwise ids checked under a
  // previous result set stay selected (invisible) and get pulled into the
  // download instead of the rows now on screen.
  useEffect(() => { setSelected(new Set()); }, [queryParams]);

  function toggle(id: number) {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    setSelected(s => (s.size === rows.length ? new Set() : new Set(rows.map(r => r.id))));
  }

  async function download() {
    const ids = selected.size > 0 ? Array.from(selected) : rows.map(r => r.id);
    if (ids.length === 0) {
      notifications.show({ message: 'Pick at least one row', color: 'yellow' });
      return;
    }
    setDownloading(true);
    try {
      const res = await api.get(`/api/train/${ids.join(',')}`, {
        params: { cls: cls ?? '', from: origin ?? '' },
        responseType: 'blob',
      });
      if (res.status !== 200) {
        notifications.show({ message: `Download failed: ${res.status}`, color: 'red' });
        return;
      }
      const blob = res.data as Blob;
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `train-booking-${(origin ?? 'all').replace(/\s+/g, '-')}-${ymd(from) ?? ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      notifications.show({ message: `Downloaded ${ids.length} legs`, color: 'green' });
    } catch (err) {
      notifications.show({
        message: 'Download failed' + (err instanceof Error ? `: ${err.message}` : ''),
        color: 'red',
      });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Stack gap="md">
      <Title order={2}>Train Booking</Title>

      <Card withBorder padding="md" radius="md">
        <Group align="flex-end" gap="md" wrap="wrap">
          <DatePickerInput
            label="From"
            value={from}
            onChange={d => setFrom(d as Date | null)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <DatePickerInput
            label="To"
            value={to}
            onChange={d => setTo(d as Date | null)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <Select
            label="Train origin"
            data={TRAIN_ORIGINS}
            value={origin}
            onChange={setOrigin}
            w={160}
          />
          <Select
            label="Class"
            data={CLASS_OPTIONS}
            value={cls}
            onChange={setCls}
            w={120}
          />
          <Button
            leftSection={<IconFileSpreadsheet size={16} />}
            onClick={download}
            disabled={rows.length === 0}
            loading={downloading}
            ml="auto"
          >
            Download xlsx
          </Button>
        </Group>
      </Card>

      <Card withBorder padding={0} radius="md">
        {queryParams == null ? (
          <Text c="dimmed" p="md">Pick a date range and train origin.</Text>
        ) : query.isPending ? (
          <Stack gap="xs" p="md">{[1, 2, 3].map(i => <Skeleton key={i} h={24} />)}</Stack>
        ) : query.isError ? (
          <Text c="red" p="md">
            Report failed — {(query.error as Error)?.message ?? 'unknown error'}
          </Text>
        ) : (
          <ScrollArea h={520}>
            <Table striped highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 32 }}>
                    <Checkbox
                      checked={rows.length > 0 && selected.size === rows.length}
                      indeterminate={selected.size > 0 && selected.size < rows.length}
                      onChange={toggleAll}
                    />
                  </Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Country</Table.Th>
                  <Table.Th>From</Table.Th>
                  <Table.Th>To</Table.Th>
                  <Table.Th>Departure</Table.Th>
                  <Table.Th>Reference</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Group justify="center" py="md" gap="xs">
                        <IconTrain size={20} />
                        <Text c="dimmed" size="sm">No train legs in that range.</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
                {rows.map(r => (
                  <Table.Tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => toggle(r.id)}>
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    </Table.Td>
                    <Table.Td>{`${r.firstName ?? ''} ${r.lastName ?? ''}`.trim()}</Table.Td>
                    <Table.Td>{r.countryName ?? ''}</Table.Td>
                    <Table.Td>{r.departureFromName ?? ''}</Table.Td>
                    <Table.Td>{r.arrivalToName ?? ''}</Table.Td>
                    <Table.Td>{formatDate(r.departureDate ?? null)}</Table.Td>
                    <Table.Td>{r.transportReference ?? ''}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!m) return iso;
  const [y, mo, d] = m[1]!.split('-');
  return `${d}/${mo}/${y!.slice(2)}`;
}
